import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import api from '../../../api/axios';
import { CheckCircleIcon, TrashIcon, UserGroupIcon, PencilIcon, ArrowUpTrayIcon, UserPlusIcon } from '@heroicons/react/24/outline';
import AssignSevakModal from '../../../components/AssignSevakModal';
import UploadCSVModal from '../../../components/UploadCSVModal';

const BusinessLeads = () => {
    const [leads, setLeads] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    
    // Modal states
    const [isAssignModalOpen, setAssignModalOpen] = useState(false);
    const [isUploadModalOpen, setUploadModalOpen] = useState(false);
    const [selectedLead, setSelectedLead] = useState(null);

    const fetchLeads = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get('/crm/leads/businesses');
            setLeads(res.data);
        } catch (err) {
            setError('Could not fetch business leads.');
            console.error(err);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchLeads(); }, [fetchLeads]);

    const handleConvert = async (id) => {
        await api.put(`/crm/business/${id}/convert_to_customer`);
        fetchLeads();
    };
    
    const handleDelete = async (id) => {
        if (window.confirm("Are you sure you want to permanently delete this lead?")) {
            await api.delete(`/crm/business/delete/${id}`);
            fetchLeads();
        }
    };
    
    const openAssignModal = (lead) => {
        setSelectedLead(lead);
        setAssignModalOpen(true);
    };

    if (loading) return <div className="p-4">Loading business leads...</div>;
    if (error) return <div className="p-4 text-red-500">{error}</div>;

    return (
        <>
            <div className="flex justify-end items-center gap-4 mb-4">
                <Link to="/business/new" className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700">
                    <UserPlusIcon className="h-5 w-5 mr-2" />
                    Add Business Lead
                </Link>
                <button onClick={() => setUploadModalOpen(true)} className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-gray-600 rounded-md hover:bg-gray-700">
                    <ArrowUpTrayIcon className="h-5 w-5 mr-2" />
                    Upload CSV
                </button>
            </div>
            
            <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                        <tr>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Lead Name</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Sevak</th>
                            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Added</th>
                            <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                        {leads.map((lead) => (
                            <tr key={lead.id}>
                                <td className="px-6 py-4">{lead.name}</td>
                                <td className="px-6 py-4">{lead.assigned_admin_name}</td>
                                <td className="px-6 py-4">{new Date(lead.created_at).toLocaleDateString()}</td>
                                <td className="px-6 py-4 whitespace-nowrap text-center text-sm font-medium space-x-4">
                                    <button onClick={() => handleConvert(lead.id)} className="text-green-600 hover:text-green-900" title="Approve & Convert to Customer"><CheckCircleIcon className="h-5 w-5" /></button>
                                    <button onClick={() => openAssignModal(lead)} className="text-blue-600 hover:text-blue-900" title="Assign Sevak"><UserGroupIcon className="h-5 w-5" /></button>
                                    <Link to={`/business/edit/${lead.id}`} className="text-indigo-600 hover:text-indigo-900" title="Edit Lead"><PencilIcon className="h-5 w-5" /></Link>
                                    <button onClick={() => handleDelete(lead.id)} className="text-red-600 hover:text-red-900" title="Delete Lead"><TrashIcon className="h-5 w-5" /></button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>

            <AssignSevakModal 
                isOpen={isAssignModalOpen}
                onClose={() => setAssignModalOpen(false)}
                lead={selectedLead}
                onAssignSuccess={fetchLeads}
            />
            <UploadCSVModal 
                isOpen={isUploadModalOpen}
                onClose={() => setUploadModalOpen(false)}
                onUploadSuccess={fetchLeads}
            />
        </>
    );
};

export default BusinessLeads;